import { generateKeyBetween } from "fractional-indexing";
import { useState } from "react";
import { v4 } from "uuid";

import {
  type TConditionalFormattingRule,
  type TConditionalFormattingRuleOptions,
  sortRules,
  useActivePageId,
  useConditionalFormattingCache,
  useUpsertConditionalFormattingRules,
} from "../../../../data";
import type { TRangeSelection } from "../../useRangeSelectionChange";
import { rangeLabel, SelectionInfo } from "../SelectionInfo";
import { rowTypeFromPeriod } from "@package/grid-utils";
import { ConditionalFormattingRuleRow } from "./Rule";
import { useGrid } from "../../stores";
import { ActionBar } from "./ActionBar";
import { ManageTable } from "../Shared";
import { isNullish } from "remeda";
import { useOnSave } from "../ModalContainer";
import { buildRowId } from "../../../../data/formatting/helpers";
import { useUserId } from "../../../../context/auth/hooks";

export const AddConditionalFormatting = ({ selection }: { selection: TRangeSelection }) => {
  const [rules, setRules] = useState<(TConditionalFormattingRuleOptions & { changed: boolean })[]>([empty()]);
  const page_id = useActivePageId();
  const user_id = useUserId();
  const cache = useConditionalFormattingCache(page_id);
  const upsert = useUpsertConditionalFormattingRules(page_id);
  const [gridApi] = useGrid();

  useOnSave(async () => {
    const next: TConditionalFormattingRule[] = [];
    // We start from the highest priority_idx for all existing rules which are sorted in reverse order
    let priority_idx = sortRules(cache.get() ?? [])[0]?.priority_idx;
    for (const rule of rules) {
      for (const cell of selection.flat()) {
        const { changed, ...rest } = rule;

        if (rest.limit?.trim() === "" || isNullish(rest.limit)) {
          throw new Error("All rules must have a limit");
        }

        if (!changed) continue;
        const rowType = rowTypeFromPeriod(cell.period);
        priority_idx = generateKeyBetween(priority_idx, null);
        next.push({
          id: v4(),
          column_id: cell.columnId,
          row_id: buildRowId(cell.period, rowType),
          page_id,
          user_id,
          priority_idx,
          ...rest,
        });
      }
    }
    if (next.length === 0) return;
    await upsert.mutateAsync(next);

    const columns = selection.flat().map((x) => x.columnId);
    const rowNodes = selection
      .flat()
      .map((x) => gridApi?.getRowNode(x.rowId))
      .filter(Boolean);
    gridApi?.refreshCells({ columns, rowNodes, force: true });
  });

  return (
    <>
      <SelectionInfo>{rangeLabel(selection ?? [])}</SelectionInfo>
      <ManageTable className="[grid-template:auto/repeat(5,minmax(0,auto))_4.5rem]">
        <ManageTable.Header>
          <div className="">Rule</div>
          <div className="">Limit</div>
          <div className="">Formatting</div>
          <div className="">Note</div>
          <div className="truncate text-center">Stop if true</div>
          <div className="" />
        </ManageTable.Header>
        {rules.map((rule, i1) => (
          <ConditionalFormattingRuleRow
            key={i1.toString()}
            value={rule}
            pageId={page_id}
            onUpdate={(updates) =>
              setRules((rules) => rules.map((rule, i2) => (i2 === i1 ? { ...rule, ...updates, changed: true } : rule)))
            }
            onRemove={rules.length > 1 ? () => setRules((y) => y.filter((_, i2) => i2 !== i1)) : undefined}
          />
        ))}
      </ManageTable>
      <ActionBar onAdd={() => setRules((y) => [...y, empty()])} />
    </>
  );
};

const empty = () => ({
  rule: "equals" as const,
  limit: "",
  note: "",
  stop_if_true: false,
  bg_color: "",
  bold_text: false,
  invert_text_color: false,
  changed: false,
});
